import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { 
  Users, TrendingUp, Euro, Calendar, Search,
  ArrowUpRight, ArrowDownRight, BarChart3, PieChart, Filter
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { format, subDays, subMonths, startOfDay, endOfDay } from "date-fns";
import { it } from "date-fns/locale";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPieChart, Pie, Cell, Legend, LineChart, Line, Area, AreaChart
} from "recharts";

// Color palette for charts
const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4'];

// Extracted StatCard component
const AnalyticsStatCard = ({ title, value, icon: Icon, trend, trendValue, color = "#6366f1" }) => (
  <Card>
    <CardContent className="p-4 sm:pt-6">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs sm:text-sm text-slate-500 truncate">{title}</p>
          <p className="text-lg sm:text-2xl font-bold mt-1 truncate">{value}</p>
          {trend && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trend === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
              <span className="truncate">{trendValue}</span>
            </div>
          )}
        </div>
        <div className="p-2 sm:p-3 rounded-lg flex-shrink-0 ml-2" style={{ backgroundColor: `${color}15` }}>
          <Icon className="h-4 w-4 sm:h-5 sm:w-5" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AnalyticsView = ({ businessId, isAdmin = false, showAllBusinesses = false, staff = [], services = [] }) => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState("month");
  const [selectedBusiness, setSelectedBusiness] = useState(businessId || "all");
  const [businesses, setBusinesses] = useState([]);
  const [serviceTypeFilter, setServiceTypeFilter] = useState("all");
  
  // New filters
  const [staffFilter, setStaffFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  
  // Custom date range
  const [startDate, setStartDate] = useState(format(subMonths(new Date(), 1), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [useCustomDates, setUseCustomDates] = useState(false);

  // Chart data
  const [bookingsByDay, setBookingsByDay] = useState([]);
  const [bookingsByType, setBookingsByType] = useState([]);
  const [revenueByDay, setRevenueByDay] = useState([]);

  useEffect(() => {
    fetchData();
  }, [businessId, dateRange, selectedBusiness, serviceTypeFilter, staffFilter, serviceFilter, genderFilter, startDate, endDate, useCustomDates]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build query params
      let queryParams = useCustomDates 
        ? `start_date=${startDate}&end_date=${endDate}`
        : `range=${dateRange}`;
      
      if (serviceTypeFilter !== "all") {
        queryParams += `&service_type=${serviceTypeFilter}`;
      }
      
      // Add new filters
      if (staffFilter !== "all") {
        queryParams += `&staff_id=${staffFilter}`;
      }
      if (serviceFilter !== "all") {
        queryParams += `&service_id=${serviceFilter}`;
      }
      if (genderFilter !== "all") {
        queryParams += `&gender=${genderFilter}`;
      }
      
      // Determine the endpoint based on context
      const basePath = isAdmin 
        ? (selectedBusiness && selectedBusiness !== "all" 
            ? `/admin/businesses/${selectedBusiness}/analytics`
            : "/admin/analytics")
        : "/my-business/analytics";
      
      const [analyticsRes, customersRes] = await Promise.all([
        axios.get(`${API}${basePath}?${queryParams}`, { headers }),
        axios.get(`${API}${basePath}/customers?${queryParams}`, { headers })
      ]);
      
      setAnalytics(analyticsRes.data);
      setCustomers(customersRes.data);
      
      // Process chart data
      processChartData(analyticsRes.data);

      // Fetch businesses list for admin
      if (isAdmin && showAllBusinesses) {
        const bizRes = await axios.get(`${API}/admin/businesses`, { headers });
        setBusinesses(bizRes.data);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (data) => {
    // Bookings by day
    if (data?.bookings_by_day) {
      setBookingsByDay(data.bookings_by_day.map(d => ({
        date: format(new Date(d.date), 'dd/MM', { locale: it }),
        prenotazioni: d.count,
        revenue: d.revenue || 0
      })));
    }
    
    // Bookings by service type
    if (data?.bookings_by_type) {
      const typeLabels = {
        'appointment': 'Appuntamenti',
        'group_event': 'Eventi',
        'daily_rental': 'Noleggi'
      };
      setBookingsByType(data.bookings_by_type.map(d => ({
        name: typeLabels[d.type] || d.type,
        value: d.count,
        revenue: d.revenue || 0
      })));
    } else {
      // Default data if not available
      setBookingsByType([
        { name: 'Appuntamenti', value: data?.appointment_count || 0 },
        { name: 'Eventi', value: data?.event_count || 0 },
        { name: 'Noleggi', value: data?.rental_count || 0 }
      ].filter(d => d.value > 0));
    }
    
    // Revenue by day
    if (data?.revenue_by_day) {
      setRevenueByDay(data.revenue_by_day.map(d => ({
        date: format(new Date(d.date), 'dd/MM', { locale: it }),
        revenue: d.revenue
      })));
    }
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.phone?.includes(searchQuery)
  );

  const handleDateRangeChange = (value) => {
    if (value === "custom") {
      setUseCustomDates(true);
    } else {
      setUseCustomDates(false);
      setDateRange(value);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with filters */}
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2">
            <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5" />
            Analytics
          </h2>
          <p className="text-xs sm:text-sm text-slate-500">
            {useCustomDates 
              ? `${format(new Date(startDate), 'dd MMM yyyy', { locale: it })} - ${format(new Date(endDate), 'dd MMM yyyy', { locale: it })}`
              : dateRange === "week" ? "Ultimi 7 giorni"
              : dateRange === "month" ? "Ultimo mese"
              : dateRange === "year" ? "Ultimo anno"
              : "Tutto il periodo"
            }
          </p>
        </div>
        
        {/* Filters Row */}
        <div className="flex flex-wrap gap-2 items-end">
          {/* Business filter (Admin only) */}
          {isAdmin && showAllBusinesses && (
            <div className="w-full sm:w-auto">
              <Label className="text-xs mb-1 block">Azienda</Label>
              <Select value={selectedBusiness} onValueChange={setSelectedBusiness}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Seleziona azienda" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le aziende</SelectItem>
                  {businesses.map(b => (
                    <SelectItem key={b.business_id} value={b.business_id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Service Type Filter */}
          <div className="w-full sm:w-auto">
            <Label className="text-xs mb-1 block">Tipologia</Label>
            <Select value={serviceTypeFilter} onValueChange={setServiceTypeFilter}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="appointment">Appuntamenti</SelectItem>
                <SelectItem value="group_event">Eventi</SelectItem>
                <SelectItem value="daily_rental">Noleggi</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Date Range */}
          <div className="w-full sm:w-auto">
            <Label className="text-xs mb-1 block">Periodo</Label>
            <Select value={useCustomDates ? "custom" : dateRange} onValueChange={handleDateRangeChange}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Ultima settimana</SelectItem>
                <SelectItem value="month">Ultimo mese</SelectItem>
                <SelectItem value="year">Ultimo anno</SelectItem>
                <SelectItem value="all">Tutto</SelectItem>
                <SelectItem value="custom">Personalizzato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Custom Date Range */}
          {useCustomDates && (
            <>
              <div className="w-full sm:w-auto">
                <Label className="text-xs mb-1 block">Da</Label>
                <Input 
                  type="date" 
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full sm:w-[140px]"
                />
              </div>
              <div className="w-full sm:w-auto">
                <Label className="text-xs mb-1 block">A</Label>
                <Input 
                  type="date" 
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full sm:w-[140px]"
                />
              </div>
            </>
          )}
          
          {/* Staff Filter */}
          {staff && staff.length > 0 && (
            <div className="w-full sm:w-auto">
              <Label className="text-xs mb-1 block">Staff</Label>
              <Select value={staffFilter} onValueChange={setStaffFilter}>
                <SelectTrigger className="w-full sm:w-[150px]" data-testid="staff-filter">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli staff</SelectItem>
                  {staff.map(s => (
                    <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Service Filter */}
          {services && services.length > 0 && (
            <div className="w-full sm:w-auto">
              <Label className="text-xs mb-1 block">Servizio</Label>
              <Select value={serviceFilter} onValueChange={setServiceFilter}>
                <SelectTrigger className="w-full sm:w-[150px]" data-testid="service-filter">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i servizi</SelectItem>
                  {services.map(s => (
                    <SelectItem key={s.service_id} value={s.service_id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Gender Filter */}
          <div className="w-full sm:w-auto">
            <Label className="text-xs mb-1 block">Genere Cliente</Label>
            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger className="w-full sm:w-[130px]" data-testid="gender-filter">
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="male">Uomo</SelectItem>
                <SelectItem value="female">Donna</SelectItem>
                <SelectItem value="other">Altro</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <AnalyticsStatCard 
          title="Clienti Totali" 
          value={analytics?.total_customers || 0}
          icon={Users}
          color="#6366f1"
        />
        <AnalyticsStatCard 
          title="Prenotazioni" 
          value={analytics?.total_bookings || 0}
          icon={Calendar}
          trend={analytics?.bookings_trend}
          trendValue={analytics?.bookings_trend_value}
          color="#10b981"
        />
        <AnalyticsStatCard 
          title="Ricavi" 
          value={`€${(analytics?.total_revenue || 0).toFixed(2)}`}
          icon={Euro}
          trend={analytics?.revenue_trend}
          trendValue={analytics?.revenue_trend_value}
          color="#f59e0b"
        />
        <AnalyticsStatCard 
          title="Media per Cliente" 
          value={`€${(analytics?.avg_per_customer || 0).toFixed(2)}`}
          icon={TrendingUp}
          color="#ec4899"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Bookings Bar Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Prenotazioni per Giorno
            </CardTitle>
          </CardHeader>
          <CardContent>
            {bookingsByDay.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={bookingsByDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ fontSize: 12 }}
                    formatter={(value, name) => [value, name === 'prenotazioni' ? 'Prenotazioni' : 'Revenue €']}
                  />
                  <Bar dataKey="prenotazioni" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                Nessun dato disponibile
              </div>
            )}
          </CardContent>
        </Card>

        {/* Service Type Pie Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <PieChart className="h-4 w-4" />
              Distribuzione per Tipologia
            </CardTitle>
          </CardHeader>
          <CardContent>
            {bookingsByType.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <RechartsPieChart>
                  <Pie
                    data={bookingsByType}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {bookingsByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Prenotazioni']} />
                  <Legend />
                </RechartsPieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                Nessun dato disponibile
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue Line Chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Trend Ricavi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenueByDay.length > 0 || bookingsByDay.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={revenueByDay.length > 0 ? revenueByDay : bookingsByDay}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
                  <Tooltip 
                    contentStyle={{ fontSize: 12 }}
                    formatter={(value) => [`€${value.toFixed(2)}`, 'Ricavi']}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorRevenue)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                Nessun dato disponibile
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Customers Table */}
      <Card>
        <CardHeader className="pb-2 sm:pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base sm:text-lg">Clienti</CardTitle>
              <CardDescription className="text-xs sm:text-sm">{customers.length} clienti totali</CardDescription>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input 
                placeholder="Cerca cliente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 text-sm"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {filteredCustomers.length === 0 ? (
              <p className="text-center text-slate-500 py-8 text-sm">Nessun cliente trovato</p>
            ) : (
              filteredCustomers.map((customer, index) => (
                <div 
                  key={index}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-slate-50 rounded-lg gap-2"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-medium flex-shrink-0 text-sm">
                      {customer.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{customer.name || "Cliente"}</p>
                      <p className="text-xs text-slate-500 truncate">{customer.email || customer.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4 text-right pl-11 sm:pl-0">
                    <div>
                      <p className="text-xs text-slate-500">Prenotazioni</p>
                      <p className="font-semibold text-sm">{customer.booking_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Spesa Totale</p>
                      <p className="font-semibold text-green-600 text-sm">€{(customer.total_spent || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsView;
